import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getMerchants, createMerchant, updateMerchant, getAffiliations } from '../services/adminService';
import CreateMerchantForm from '../components/CreateMerchantForm';
import * as XLSX from 'xlsx';
import {
    Alert,
    Box,
    Button,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ExcelColumnFilter from '../components/ExcelColumnFilter';

const operationTypeLabelMap = {
    t: 'Retail',
    r: 'Restaurante',
    h: 'Hotel',
    a: 'Renta de auto'
};

const getOperationTypeLabel = (value) => operationTypeLabelMap[(value || '').toLowerCase()] || '-';
const getOperationTypeCode = (value) => {
    const normalized = (value || '').toLowerCase();
    if (!operationTypeLabelMap[normalized]) {
        return '-';
    }
    return normalized.toUpperCase();
};

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflow: 'auto'
};

const merchantColumns = [
    'name',
    'street',
    'number',
    'city',
    'state',
    'country',
    'zip',
    'affiliation_id'
];

const Merchants = () => {
    const [merchants, setMerchants] = useState([]);
    const [affiliations, setAffiliations] = useState([]);
    const [error, setError] = useState('');
    const [showCreateMerchantForm, setShowCreateMerchantForm] = useState(false);
    const [editingMerchant, setEditingMerchant] = useState(null);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [columnFilters, setColumnFilters] = useState({});
    const fileInputRef = useRef(null);

    const fetchMerchants = useCallback(async () => {
        try {
            const data = await getMerchants();
            setMerchants(data);
        } catch (err) {
            setError('No se pudieron obtener los comercios.');
        }
    }, []);

    const fetchAffiliations = useCallback(async () => {
        try {
            const data = await getAffiliations();
            setAffiliations(data);
        } catch (err) {
            const detail = err?.response?.data?.message || err?.response?.data || err?.message;
            setError(`No se pudieron obtener las afiliaciones.${detail ? ` Detalle: ${detail}` : ''}`);
        }
    }, []);

    useEffect(() => {
        fetchMerchants();
        fetchAffiliations();
    }, [fetchMerchants, fetchAffiliations]);

    const handleMerchantCreated = () => {
        setShowCreateMerchantForm(false);
        setEditingMerchant(null);
        fetchMerchants();
    };

    const openCreateMerchantModal = () => {
        setEditingMerchant(null);
        setShowCreateMerchantForm(true);
    };

    const openEditMerchantModal = (merchant) => {
        setEditingMerchant(merchant);
        setShowCreateMerchantForm(true);
    };

    const closeMerchantModal = () => {
        setShowCreateMerchantForm(false);
        setEditingMerchant(null);
    };

    const downloadTemplate = () => {
        const workbook = XLSX.utils.book_new();
        const templateRows = [
            {
                name: 'Comercio Demo',
                street: 'Av. Principal',
                number: '100',
                city: 'CDMX',
                state: 'CDMX',
                country: 'Mexico',
                zip: '01000',
                affiliation_id: 1
            }
        ];

        const sheet = XLSX.utils.json_to_sheet(templateRows, { header: merchantColumns });
        XLSX.utils.book_append_sheet(workbook, sheet, 'Merchants');
        XLSX.writeFile(workbook, 'merchants_template.xlsx');
    };

    const normalizeRow = (row) => {
        const normalized = {};
        Object.keys(row || {}).forEach((key) => {
            const normalizedKey = key.toString().trim().toLowerCase().replace(/\s+/g, '_');
            normalized[normalizedKey] = row[key];
        });
        return normalized;
    };

    const handleBulkUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setError('');
        setBulkResult(null);
        setBulkLoading(true);

        try {
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[firstSheetName];

            if (!sheet) {
                throw new Error('No se encontró ninguna hoja en el archivo.');
            }

            const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            const rows = rawRows.map(normalizeRow);

            if (rows.length === 0) {
                throw new Error('El archivo está vacío.');
            }

            const missingColumns = merchantColumns.filter(
                (column) => !Object.keys(rows[0]).includes(column)
            );

            if (missingColumns.length > 0) {
                throw new Error(`Faltan columnas obligatorias: ${missingColumns.join(', ')}`);
            }

            const failures = [];
            let successCount = 0;

            for (let index = 0; index < rows.length; index += 1) {
                const row = rows[index];
                const payload = merchantColumns.reduce((accumulator, column) => {
                    accumulator[column] = (row[column] ?? '').toString().trim();
                    return accumulator;
                }, {});

                const affiliationId = Number.parseInt(payload.affiliation_id, 10);
                payload.affiliation_id = affiliationId;

                const hasEmptyField = merchantColumns.some((column) => {
                    if (column === 'affiliation_id') {
                        return false;
                    }
                    return !payload[column];
                });

                if (hasEmptyField) {
                    failures.push({
                        row: index + 2,
                        message: 'Uno o más campos obligatorios están vacíos.'
                    });
                    continue;
                }

                if (Number.isNaN(affiliationId) || affiliationId <= 0) {
                    failures.push({
                        row: index + 2,
                        message: 'affiliation_id debe ser numérico y mayor a 0.'
                    });
                    continue;
                }

                try {
                    await createMerchant(payload);
                    successCount += 1;
                } catch (requestError) {
                    failures.push({
                        row: index + 2,
                        message: requestError?.response?.data?.message || 'No se pudo crear el comercio.'
                    });
                }
            }

            setBulkResult({
                total: rows.length,
                successCount,
                failureCount: failures.length,
                failures
            });

            fetchMerchants();
        } catch (uploadError) {
            setError(uploadError.message || 'No se pudo procesar el archivo cargado.');
        } finally {
            setBulkLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const updateColumnFilter = (column, values) => {
        setColumnFilters((prev) => ({
            ...prev,
            [column]: values
        }));
    };

    const clearFilters = () => setColumnFilters({});

    const getAffiliationById = (affiliationId) => affiliations.find((item) => item.id === affiliationId);

    const getAffiliationLabel = (affiliationId) => {
        const affiliation = getAffiliationById(affiliationId);
        if (!affiliation) {
            return `ID ${affiliationId || '-'}`;
        }
        return affiliation.nombre || '-';
    };

    const getAffiliationTypeCode = (affiliationId) => {
        const affiliation = getAffiliationById(affiliationId);
        return getOperationTypeCode(affiliation?.tipo_operacion);
    };

    const getAffiliationTypeName = (affiliationId) => {
        const affiliation = getAffiliationById(affiliationId);
        return getOperationTypeLabel(affiliation?.tipo_operacion);
    };

    const getAffiliationDeviceId = (affiliationId) => {
        const affiliation = getAffiliationById(affiliationId);
        return affiliation?.device_id || '-';
    };

    const getColumnValue = (merchant, column) => {
        if (column === 'id') {
            return (merchant.id ?? '').toString();
        }
        if (column === 'name') {
            return merchant.name || '';
        }
        if (column === 'affiliation') {
            return getAffiliationLabel(merchant.affiliation_id);
        }
        if (column === 'typeCode') {
            return getAffiliationTypeCode(merchant.affiliation_id);
        }
        if (column === 'typeName') {
            return getAffiliationTypeName(merchant.affiliation_id);
        }
        if (column === 'deviceId') {
            return getAffiliationDeviceId(merchant.affiliation_id);
        }
        if (column === 'city') {
            return merchant.city || '';
        }
        if (column === 'country') {
            return merchant.country || '';
        }
        return '';
    };

    const matchesColumnFilter = (value, selectedOptions) => {
        if (selectedOptions === undefined) {
            return true;
        }
        return selectedOptions.includes(value);
    };

    const filteredMerchants = merchants.filter((merchant) => {
        return (
            matchesColumnFilter(getColumnValue(merchant, 'id'), columnFilters.id) &&
            matchesColumnFilter(getColumnValue(merchant, 'name'), columnFilters.name) &&
            matchesColumnFilter(getColumnValue(merchant, 'affiliation'), columnFilters.affiliation) &&
            matchesColumnFilter(getColumnValue(merchant, 'typeCode'), columnFilters.typeCode) &&
            matchesColumnFilter(getColumnValue(merchant, 'typeName'), columnFilters.typeName) &&
            matchesColumnFilter(getColumnValue(merchant, 'deviceId'), columnFilters.deviceId) &&
            matchesColumnFilter(getColumnValue(merchant, 'city'), columnFilters.city) &&
            matchesColumnFilter(getColumnValue(merchant, 'country'), columnFilters.country)
        );
    });

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1">
                    Comercios
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
                        Descargar plantilla
                    </Button>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        disabled={bulkLoading}
                    >
                        {bulkLoading ? 'Cargando...' : 'Subir Excel'}
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept=".xlsx,.xls,.csv"
                            onChange={handleBulkUpload}
                        />
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateMerchantModal}>
                        Agregar comercio
                    </Button>
                    <Button variant="text" onClick={clearFilters}>Limpiar filtros</Button>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    Campos obligatorios de carga masiva: name, street, number, city, state, country, zip, affiliation_id.
                </Typography>
                <Typography variant="body2">
                    Antes de crear comercios, registra afiliaciones en la sección de Afiliaciones para obtener los IDs válidos.
                </Typography>
            </Alert>

            {error && <Typography color="error">{error}</Typography>}
            {bulkResult && (
                <Alert severity={bulkResult.failureCount > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    Se procesaron {bulkResult.total} filas: {bulkResult.successCount} creadas, {bulkResult.failureCount} con error.
                    {bulkResult.failureCount > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {bulkResult.failures.slice(0, 5).map((failure) => (
                                <Typography key={`${failure.row}-${failure.message}`} variant="body2">
                                    Fila {failure.row}: {failure.message}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Alert>
            )}

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="ID"
                                    options={merchants.map((item) => getColumnValue(item, 'id'))}
                                    selectedOptions={columnFilters.id}
                                    onChange={(values) => updateColumnFilter('id', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Nombre"
                                    options={merchants.map((item) => getColumnValue(item, 'name'))}
                                    selectedOptions={columnFilters.name}
                                    onChange={(values) => updateColumnFilter('name', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Afiliación"
                                    options={merchants.map((item) => getColumnValue(item, 'affiliation'))}
                                    selectedOptions={columnFilters.affiliation}
                                    onChange={(values) => updateColumnFilter('affiliation', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Tipo (código)"
                                    options={merchants.map((item) => getColumnValue(item, 'typeCode'))}
                                    selectedOptions={columnFilters.typeCode}
                                    onChange={(values) => updateColumnFilter('typeCode', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Tipo afiliación"
                                    options={merchants.map((item) => getColumnValue(item, 'typeName'))}
                                    selectedOptions={columnFilters.typeName}
                                    onChange={(values) => updateColumnFilter('typeName', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Device ID afiliación"
                                    options={merchants.map((item) => getColumnValue(item, 'deviceId'))}
                                    selectedOptions={columnFilters.deviceId}
                                    onChange={(values) => updateColumnFilter('deviceId', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Ciudad"
                                    options={merchants.map((item) => getColumnValue(item, 'city'))}
                                    selectedOptions={columnFilters.city}
                                    onChange={(values) => updateColumnFilter('city', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="País"
                                    options={merchants.map((item) => getColumnValue(item, 'country'))}
                                    selectedOptions={columnFilters.country}
                                    onChange={(values) => updateColumnFilter('country', values)}
                                />
                            </TableCell>
                            <TableCell>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredMerchants.map((merchant) => (
                            <TableRow key={merchant.id}>
                                <TableCell>{merchant.id}</TableCell>
                                <TableCell>{merchant.name}</TableCell>
                                <TableCell>{getAffiliationLabel(merchant.affiliation_id)}</TableCell>
                                <TableCell>{getAffiliationTypeCode(merchant.affiliation_id)}</TableCell>
                                <TableCell>{getAffiliationTypeName(merchant.affiliation_id)}</TableCell>
                                <TableCell>{getAffiliationDeviceId(merchant.affiliation_id)}</TableCell>
                                <TableCell>{merchant.city}</TableCell>
                                <TableCell>{merchant.country}</TableCell>
                                <TableCell>
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEditMerchantModal(merchant)}>
                                        Editar
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal open={showCreateMerchantForm} onClose={closeMerchantModal}>
                <Box sx={style}>
                    <CreateMerchantForm
                        onMerchantCreated={handleMerchantCreated}
                        createMerchant={editingMerchant ? (merchantData) => updateMerchant(editingMerchant.id, merchantData) : createMerchant}
                        affiliations={affiliations}
                        initialValues={editingMerchant}
                        title={editingMerchant ? 'Editar comercio' : 'Crear nuevo comercio'}
                        submitLabel={editingMerchant ? 'Guardar cambios' : 'Crear'}
                    />
                </Box>
            </Modal>
        </Paper>
    );
};

export default Merchants;
